import React, { useState, useEffect, useReducer } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import DealListRepository from '../../../repository/DealListRepository';
import DealItem from '../Item';
import { GamifyIcon } from '../../../components/GamifyIcon';
import { SearchInput } from '../../users/StyledInputs';
import fetchDealStages from '../fetchDealStages';
import fetchPeople from '../../teams/fetchPeople';
import fetchCirclesOrPeople from '../fetchCirclesOrPeople';
import DealsKanbanSkeleton from './DealsKanbanSkeleton.view';
import { DealsByDealStage, Deal } from './DealsKanban.types';
import getUserAvatar from '../../../common/utils/getUserAvatar';
import DealKanbanCard from './components/DealKanbanCard';
import * as queries from '../../../../graphql/queries';
import { API } from 'aws-amplify';
import OwnerDealStageModal from '../OwnerDealStageModal';
import { GamifyToast } from '../../../common/CustomToasts';

export default function DealsKanban() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchString, setSearchString] = useState<string>('');
  const [searchStage, setSearchStage] = useState<string>('all');
  const [userId, setUserId] = useState<string>(
    // @ts-ignore
    global.me.isAdmin
      ? 'all'
      : // @ts-ignore
        global.me.id
  );
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);

  // NOTE: These are needed to reuse the SelectedDealDisplay from DealsList
  const [deals, setDeals] = useState([]);
  const [dealsNextToken, setDealsNextToken] = useState(null);
  const [stageTitle, setStageTitle] = useState('');
  const [dealTypes, setDealTypes] = useState<any[]>([]);
  const [dealTypesCategoryIdMap, setDealTypesCategoryIdMap] = useState({});
  const [dealTypesConfigMap, setDealTypesConfigMap] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [selectedDealMetaDataOpen, setSelectedDealMetaDataOpen] =
    useState(false);
  const [fromDealPage, setFromDealPage] = useState(0);

  const [categoryId, setCategoryID] = useState<string>('');
  const [dealStages, setDealStages] = useState<any[]>([]);
  const [normalizedDealStages, setNormalizedDealStages] = useState<any>({});
  const [areDealStagesLoaded, setAreDealStagesLoaded] =
    useState<boolean>(false);
  const [dealPeople, setDealPeople] = useState<any[]>([]);

  // Essentially always false so unused state variables (only needed for DealsList, but they're integrated into SelectedDealDisplay...)
  if (dealTypes.length === 1000) {
    console.log(
      deals,
      dealsNextToken,
      stageTitle,
      isLoaded,
      paginationLoading,
      addDealOpen,
      sortOpen,
      setFromDealPage
    );
    console.log(categoryId, dealStages, setSearchStage);
  }

  //   Use in useEffect for refetching deals
  const [dealFilter, setDealFilter] = useState({
    dealType: 'all', // TO DO: Is all even possible? Default to first valid dealType in dealTypes if not.
    dealSort: 'date-desc',
    dealStage: 'all',
    dealOwner: 'all',
  });

  const [showOwnerDealStageModal, setShowOwnerDealStageModal] =
    useState<boolean>(false);

  const initialState: DealsByDealStage = {};

  const SET_DEALS = 'SET_DEALS';
  const UPDATE_DEAL_STAGE = 'UPDATE_DEAL_STAGE';

  function dealsReducer(state: DealsByDealStage, action: any) {
    switch (action.type) {
      case SET_DEALS:
        return { ...state, ...action.payload };
      case UPDATE_DEAL_STAGE: {
        const { stageId, updates } = action.payload;
        return {
          ...state,
          [stageId]: {
            ...state[stageId],
            ...updates,
          },
        };
      }
      default:
        return state;
    }
  }

  const [dealsByDealStage, dispatch] = useReducer(dealsReducer, initialState);

  const [triggerDealsRefetch, setTriggerDealsRefetch] = useState<number>(0);
  const onTriggerDealsRefetch = () => {
    setTriggerDealsRefetch((refetch) => refetch + 1);
  };

  const [triggerSelectedDealRefetch, setTriggerSelectedDealRefetch] =
    useState<number>(0);
  const onTriggerSelectedDealRefetch = () => {
    setTriggerSelectedDealRefetch((refetch) => refetch + 1);
  };

  // NOTE: getData will be replaced with new lambda
  async function getData(
    userId: any,
    searchStage: any,
    categoryID: any,
    sortOrder: any,
    searchString: any,
    nextToken: any,
    resetList: any,
    from: any
    // dealDispositions
  ) {
    DealListRepository.fetchDeals(
      userId,
      searchStage,
      categoryID,
      sortOrder,
      searchString,
      nextToken,
      resetList,
      from
      // dealDispositions
    ).then((_deals) => {
      // console.log('_deals: ', _deals);
      if (!_deals || _deals.length !== 0) {
        const tempDeals = _deals[0];

        // console.log('tempDeals: ', tempDeals);

        setDeals(tempDeals);
        // console.log('_deals[1] Stage Title: ', _deals[1]);
        setStageTitle(_deals[1]);
        // console.log('_deals[2] Deals Next Token: ', _deals[2]);
        setDealsNextToken(_deals[2]);

        const tempStageCategories = _deals[3];
        const dealTypesFromStageCategories = [];
        const mapDealTypeToCategoryId = {};

        if (tempDeals.length > 0) {
          for (let i = 0; i < tempStageCategories.length; i++) {
            if (tempStageCategories[i].enableDeal) {
              dealTypesFromStageCategories.push(tempStageCategories[i].title);
              // @ts-ignore
              mapDealTypeToCategoryId[tempStageCategories[i].title] =
                tempStageCategories[i].id;
            }
          }
        }

        // Conditional is needed to prevent losing those 4 deal types states... only set on original query when category id is ''
        if (tempStageCategories.length > 0) {
          // console.log('deal types SHOULD BE SETTING: ', tempStageCategories);
          setDealTypes(dealTypesFromStageCategories);
          setDealTypesCategoryIdMap(mapDealTypeToCategoryId);
          setDealTypesConfigMap(_deals[4]);
        }

        // // NO LONGER NEEDED
        // setDealDispositions(_deals[4]);
      }
      setIsLoaded(true);
      setPaginationLoading(false);
    });
  }

  // NOTE: Info from getDealTypes will eventually be done in getMyInfo
  async function getDealTypes() {
    const _dealTypes = await DealListRepository.fetchDealTypes();
    const [defaultCategoryID, stageCategories] = _dealTypes;

    for (const stageCategory of stageCategories) {
      if (stageCategory.id === defaultCategoryID) {
        setCategoryID(defaultCategoryID);
        setDealFilter((p) => ({
          ...p,
          dealType: stageCategory.title,
        }));
        break;
      }
    }

    return defaultCategoryID;
  }

  const handleNewDeal = (
    e: any,
    dealType: any,
    dealTypesCategoryIdMap: any,
    dealTypesConfigMap: any
  ) => {
    e.preventDefault();

    // NOTE: If a modal is used there's no need to navigate to /pipeline/new... modal still doesn't work so still navigate
    // setShowNewDealFormModal(true);
    navigate('/pipeline/new', {
      // TO DO: add in other props for DealFormPage, see CreateDealScreen component in lyferize MyDealScreen.tsx for reference
      state: {
        dealType,
        dealTypesCategoryIdMap,
        dealTypesConfigMap,
        isOnKanban: true,
      },
    });
  };

  const handleClearSearch = () => {
    setSearchString('');
  };

  const handleDealSelection = (e: any, dealId: string, deal: Deal) => {
    e.preventDefault();
    setSelectedDealMetaDataOpen(false);
    // setSelectedDealOptionsOpen(false);
    setSelectedDeal(deal);
    navigate(`/pipeline/kanban/${dealId}`);
  };

  const handleOpenOwnerDealStageModal = () => {
    setShowOwnerDealStageModal(true);
  };

  const handleLoadMore = async (dealStageId: string) => {
    const currentDeals = dealsByDealStage[dealStageId].deals;
    const currentOffset = dealsByDealStage[dealStageId].currentOffset;

    // Start the loading
    const dealsByDealStageLoadingPayload = {
      stageId: dealStageId,
      updates: {
        deals: currentDeals,
        currentOffset,
        canLoadMore: true,
        isLoading: true,
      },
    };

    dispatch({
      type: UPDATE_DEAL_STAGE,
      payload: dealsByDealStageLoadingPayload,
    });

    const displayFields =
      // @ts-ignore
      dealTypesConfigMap[dealTypes[0]].pickerDisplayFields;
    let deals = [];
    let canLoadMore;

    try {
      const res = await API.graphql({
        query: queries.listAllDeals,
        variables: {
          request: JSON.stringify({
            categoryID: categoryId,
            stageID: dealStageId,
            limit: 25,
            offset: currentOffset,
            displayFields,
          }),
        },
      });
      // @ts-ignore
      const parsedData = JSON.parse(res.data.listAllDeals);
      deals = parsedData.deals || [];
      canLoadMore = parsedData.canLoadMore;
    } catch (err) {
      console.error(err);

      GamifyToast.error(
        'There was an error loading more deals for this stage.'
      );

      // Stop the loading animation, put back the deals that were there before attempting to load more
      const dealsByDealStageStopLoadingPayload = {
        stageId: dealStageId,
        updates: {
          deals: currentDeals,
          currentOffset,
          canLoadMore: false,
          isLoading: false,
        },
      };

      dispatch({
        type: UPDATE_DEAL_STAGE,
        payload: dealsByDealStageStopLoadingPayload,
      });
    }

    const dealsArr: Deal[] = [...currentDeals];

    for (const deal of deals) {
      // Need to get setter/closer profile pictures from imageName (if no imageName, will be an empty string)
      const setterProfilePicture = await getUserAvatar(
        deal.setter.imageName,
        '',
        true
      );
      // @ts-ignore
      deal.setter.profilePicture = setterProfilePicture;

      let closerProfilePicture;

      if (deal.closer) {
        closerProfilePicture = await getUserAvatar(
          deal.closer.imageName,
          '',
          true
        );
        // @ts-ignore
        deal.closer.profilePicture = closerProfilePicture;
      }

      const tempLocalDate = new Date(deal.updatedAt);
      // NOTE: getTimezoneOffset gives the offset from UTC in minutes, converting to ms by multiplying by 60000 is needed
      const timezoneOffset = tempLocalDate.getTimezoneOffset() * 60000;
      // NOTE: timezoneOffset is positive for west of UTC, negative for east of UTC -> subtract this offset to get local time
      const adjustedDate = new Date(tempLocalDate.getTime() - timezoneOffset);
      // NOTE: toISOString converts the Date object to UTC, since adjustedDate was offset this conversion will make adjustedUpdatedAt the user's local date time
      const adjustedUpdatedAt = adjustedDate.toISOString();

      const unformattedUpdatedAt = adjustedUpdatedAt.split('T');
      const dealYear = unformattedUpdatedAt[0].slice(0, 4);
      const dealMonth = unformattedUpdatedAt[0].slice(5, 7);
      const dealDay = unformattedUpdatedAt[0].slice(8, 10);
      const dealHour = unformattedUpdatedAt[1].slice(0, 2);
      const dealMinute = unformattedUpdatedAt[1].slice(3, 5);
      const updatedAt = `${dealMonth}/${dealDay}/${dealYear} ${dealHour}:${dealMinute}`;

      deal.updatedAt = updatedAt;

      dealsArr.push(deal);
    }

    const dealsByDealStageUpdatePayload = {
      stageId: dealStageId,
      updates: {
        deals: dealsArr,
        currentOffset: dealsArr.length,
        canLoadMore,
        isLoading: false,
      },
    };

    dispatch({
      type: UPDATE_DEAL_STAGE,
      payload: dealsByDealStageUpdatePayload,
    });
  };

  const cancelIconAdornment =
    searchString.length > 0 ? (
      <Button
        className={'clear-search-button'}
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
        onClick={handleClearSearch}
        disableRipple={true}
      >
        <InputAdornment position="end">
          <GamifyIcon
            icon={'delete'}
            color={'white'}
            backgroundColor={'#D0D2D8'}
            height={24}
            width={24}
          />
        </InputAdornment>
      </Button>
    ) : (
      <></>
    );

  // Default value for Owner filter
  let defaultOwner = '';
  // @ts-ignore
  if (global.me.isAdmin) {
    // @ts-ignore
    defaultOwner = 'All ' + global.appSettings.labels.person.plural;
  } else {
    // @ts-ignore
    defaultOwner = global.me.name;
  }

  useEffect(() => {
    // @ts-ignore
    setSelectedDealId(params.dealId);
  }, [params?.dealId]);

  // NOTE: Needed to populate the dealType maps for the DealFormPage
  //   TO DO: Move dealType maps fetch from getData into separate fetch, move to other useEffect below
  useEffect(() => {
    async function prepareDeals() {
      await getData(
        userId,
        searchStage,
        // categoryId,
        '',
        // 'date-desc',
        dealFilter.dealSort,
        searchString,
        null,
        true,
        fromDealPage * 25
        // dealDispositions
      );
    }

    prepareDeals();
  }, []);

  useEffect(() => {
    async function prepareFilters() {
      // NOTE: getDealTypes sets default deal type in dealFilter's type key and sets categoryID to the defaultCategoryID when more than one possible deal type
      const tempDefaultCategoryID = await getDealTypes();

      const tempDealStages = await fetchDealStages(
        tempDefaultCategoryID,
        true, // showAll
        false, // activeOptiom
        false, // allOption
        null, // minSequence
        true // ignoreNoDealStages
      );

      // Set up dealStages in dealsByDealStage as loading until lambda called for each dealStage
      const tempDealsByDealStage = {};

      for (const dealStage of tempDealStages) {
        // @ts-ignore
        tempDealsByDealStage[dealStage.value] = {
          deals: [],
          currentOffset: 0,
          canLoadMore: false,
          isLoading: true,
        };
      }

      dispatch({ type: SET_DEALS, payload: tempDealsByDealStage });

      // @ts-ignore
      const allOption = global.me.isAdmin;
      const useChildCirclesList = true;

      // @ts-ignore
      if (global.me.isAdmin) {
        const tempPeople = await fetchPeople('', allOption);
        setDealPeople(tempPeople || []);
      } else {
        let pickUsers, pickCircles, useCirclesList;

        // @ts-ignore
        if (!global.appSettings.features.deal.disableUserFilter) {
          pickUsers = true;
        }
        // @ts-ignore
        if (!global.appSettings.features.deal.disableCircleFilter) {
          pickCircles = true;
          useCirclesList = true;
        }

        const tempCirclesOrPeople = await fetchCirclesOrPeople(
          '',
          allOption,
          pickUsers,
          pickCircles,
          useCirclesList,
          useChildCirclesList
        );
        setDealPeople(tempCirclesOrPeople);
      }

      const tempNormalizedDealStages = tempDealStages.reduce(
        (acc: any, stage: any) => {
          acc[stage.id] = stage;
          return acc;
        },
        {}
      );

      setCategoryID(tempDefaultCategoryID);
      setDealStages(tempDealStages);
      setNormalizedDealStages(tempNormalizedDealStages);
      setAreDealStagesLoaded(true);
    }
    prepareFilters();
  }, []);

  // Initial deal setup AND for if dealStages or dealTypes change
  useEffect(() => {
    // const tempDealsByDealStage = {};
    // console.log('LAMBDA TEST dealStages: ', dealStages);
    // console.log('LAMBDA TEST dealTypes: ', dealTypes);

    async function setUpDeals() {
      const displayFields =
        // @ts-ignore
        dealTypesConfigMap[dealTypes[0]].pickerDisplayFields;

      // Update deals for each stage
      for (const dealStage of dealStages) {
        // console.log('LAMBDA TEST dealStage: ', dealStage.name);
        // console.log('LAMBDA TEST dealStage id: ', dealStage.id);
        let deals = [];
        let canLoadMore;

        try {
          const res = await API.graphql({
            query: queries.listAllDeals,
            variables: {
              request: JSON.stringify({
                categoryID: categoryId,
                stageID: dealStage.id,
                limit: 25,
                displayFields,
              }),
            },
          });
          // @ts-ignore
          const parsedData = JSON.parse(res.data.listAllDeals);
          //   console.log('LAMBDA TEST res', parsedData);
          //   console.log('LAMBDA TEST can load more', parsedData.canLoadMore);
          //   for (const deal of parsedData.deals) {
          //     console.log('TEST deal', deal);
          //   }
          deals = parsedData.deals || [];
          canLoadMore = parsedData.canLoadMore;
        } catch (err) {
          //   console.log(
          //     'LAMBDA TEST err dealStage and id: ',
          //     dealStage.name,
          //     dealStage.id
          //   );
          //   console.log('LAMBDA TEST err', err);
          console.error(err);

          GamifyToast.error(
            `There was an error loading more deals for the ${
              normalizedDealStages[dealStage.id].name
            } stage.`
          );

          // Stop the loading animation, set deals to empty array
          const dealsByDealStageStopLoadingPayload = {
            stageId: dealStage.id,
            updates: {
              deals: [],
              currentOffset: 0,
              canLoadMore: false,
              isLoading: false,
            },
          };

          dispatch({
            type: UPDATE_DEAL_STAGE,
            payload: dealsByDealStageStopLoadingPayload,
          });
        }

        const dealsArr: Deal[] = [];

        for (const deal of deals) {
          // Need to get setter/closer profile pictures from imageName (if no imageName, will be an empty string)
          const setterProfilePicture = await getUserAvatar(
            deal.setter.imageName,
            '',
            true
          );
          // @ts-ignore
          deal.setter.profilePicture = setterProfilePicture;

          let closerProfilePicture;

          if (deal.closer) {
            closerProfilePicture = await getUserAvatar(
              deal.closer.imageName,
              '',
              true
            );
            // @ts-ignore
            deal.closer.profilePicture = closerProfilePicture;
          }

          const tempLocalDate = new Date(deal.updatedAt);
          // NOTE: getTimezoneOffset gives the offset from UTC in minutes, converting to ms by multiplying by 60000 is needed
          const timezoneOffset = tempLocalDate.getTimezoneOffset() * 60000;
          // NOTE: timezoneOffset is positive for west of UTC, negative for east of UTC -> subtract this offset to get local time
          const adjustedDate = new Date(
            tempLocalDate.getTime() - timezoneOffset
          );
          // NOTE: toISOString converts the Date object to UTC, since adjustedDate was offset this conversion will make adjustedUpdatedAt the user's local date time
          const adjustedUpdatedAt = adjustedDate.toISOString();

          const unformattedUpdatedAt = adjustedUpdatedAt.split('T');
          const dealYear = unformattedUpdatedAt[0].slice(0, 4);
          const dealMonth = unformattedUpdatedAt[0].slice(5, 7);
          const dealDay = unformattedUpdatedAt[0].slice(8, 10);
          const dealHour = unformattedUpdatedAt[1].slice(0, 2);
          const dealMinute = unformattedUpdatedAt[1].slice(3, 5);
          const updatedAt = `${dealMonth}/${dealDay}/${dealYear} ${dealHour}:${dealMinute}`;

          deal.updatedAt = updatedAt;

          dealsArr.push(deal);
        }

        const dealsByDealStageUpdatePayload = {
          stageId: dealStage.value,
          updates: {
            deals: dealsArr,
            currentOffset: dealsArr.length,
            canLoadMore,
            isLoading: false,
          },
        };

        dispatch({
          type: UPDATE_DEAL_STAGE,
          payload: dealsByDealStageUpdatePayload,
        });
      }
    }

    // Only setUpDeals once dealStages and dealTypes are loaded
    if (dealStages.length > 0 && dealTypes.length > 0) {
      setUpDeals();
    }
  }, [dealStages, dealTypes]);

  useEffect(() => {
    async function setUpFilteredDeals() {
      const displayFields =
        // @ts-ignore
        dealTypesConfigMap[dealTypes[0]].pickerDisplayFields;

      // Update deals for each stage
      for (const dealStage of dealStages) {
        const tempDealsByDealStage = {};
        // @ts-ignore
        tempDealsByDealStage[dealStage.value] = {
          deals: [],
          currentOffset: 0,
          canLoadMore: false,
          isLoading: true,
        };

        dispatch({ type: SET_DEALS, payload: tempDealsByDealStage });

        let deals = [];
        let canLoadMore;

        try {
          const res = await API.graphql({
            query: queries.listAllDeals,
            variables: {
              request: JSON.stringify({
                categoryID: categoryId,
                stageID: dealStage.id,
                assigneeID:
                  dealFilter.dealOwner === 'all' ? '' : dealFilter.dealOwner,
                searchString,
                limit: 25,
                displayFields,
              }),
            },
          });
          // @ts-ignore
          const parsedData = JSON.parse(res.data.listAllDeals);
          deals = parsedData.deals || [];
          canLoadMore = parsedData.canLoadMore;
        } catch (err) {
          console.error(err);

          GamifyToast.error(
            `There was an error loading more deals for the ${
              normalizedDealStages[dealStage.id].name
            } stage.`
          );

          // Stop the loading animation, set deals to empty array
          const dealsByDealStageStopLoadingPayload = {
            stageId: dealStage.id,
            updates: {
              deals: [],
              currentOffset: 0,
              canLoadMore: false,
              isLoading: false,
            },
          };

          dispatch({
            type: UPDATE_DEAL_STAGE,
            payload: dealsByDealStageStopLoadingPayload,
          });
        }

        const dealsArr: Deal[] = [];

        for (const deal of deals) {
          // Need to get setter/closer profile pictures from imageName (if no imageName, will be an empty string)
          const setterProfilePicture = await getUserAvatar(
            deal.setter.imageName,
            '',
            true
          );
          // @ts-ignore
          deal.setter.profilePicture = setterProfilePicture;

          let closerProfilePicture;

          if (deal.closer) {
            closerProfilePicture = await getUserAvatar(
              deal.closer.imageName,
              '',
              true
            );
            // @ts-ignore
            deal.closer.profilePicture = closerProfilePicture;
          }

          const tempLocalDate = new Date(deal.updatedAt);
          // NOTE: getTimezoneOffset gives the offset from UTC in minutes, converting to ms by multiplying by 60000 is needed
          const timezoneOffset = tempLocalDate.getTimezoneOffset() * 60000;
          // NOTE: timezoneOffset is positive for west of UTC, negative for east of UTC -> subtract this offset to get local time
          const adjustedDate = new Date(
            tempLocalDate.getTime() - timezoneOffset
          );
          // NOTE: toISOString converts the Date object to UTC, since adjustedDate was offset this conversion will make adjustedUpdatedAt the user's local date time
          const adjustedUpdatedAt = adjustedDate.toISOString();

          const unformattedUpdatedAt = adjustedUpdatedAt.split('T');
          const dealYear = unformattedUpdatedAt[0].slice(0, 4);
          const dealMonth = unformattedUpdatedAt[0].slice(5, 7);
          const dealDay = unformattedUpdatedAt[0].slice(8, 10);
          const dealHour = unformattedUpdatedAt[1].slice(0, 2);
          const dealMinute = unformattedUpdatedAt[1].slice(3, 5);
          const updatedAt = `${dealMonth}/${dealDay}/${dealYear} ${dealHour}:${dealMinute}`;

          deal.updatedAt = updatedAt;

          dealsArr.push(deal);
        }

        const dealsByDealStageUpdatePayload = {
          stageId: dealStage.value,
          updates: {
            deals: dealsArr,
            currentOffset: dealsArr.length,
            canLoadMore,
            isLoading: false,
          },
        };

        dispatch({
          type: UPDATE_DEAL_STAGE,
          payload: dealsByDealStageUpdatePayload,
        });
      }
    }

    // Only setUpDeals once dealStages and dealTypes are loaded
    if (dealStages.length > 0 && dealTypes.length > 0) {
      setUpFilteredDeals();
    }
  }, [dealFilter, triggerDealsRefetch]);

  // NOTE: For searching, functionality should match filter but NEEDS to be in setTimeout so that the search occurs once the user is done typing
  useEffect(() => {
    const setUpSearchedDeals = setTimeout(async () => {
      const displayFields =
        // @ts-ignore
        dealTypesConfigMap[dealTypes[0]].pickerDisplayFields;

      // Update deals for each stage
      for (const dealStage of dealStages) {
        const tempDealsByDealStage = {};
        // @ts-ignore
        tempDealsByDealStage[dealStage.value] = {
          deals: [],
          currentOffset: 0,
          canLoadMore: false,
          isLoading: true,
        };

        dispatch({ type: SET_DEALS, payload: tempDealsByDealStage });

        let deals = [];
        let canLoadMore;

        try {
          const res = await API.graphql({
            query: queries.listAllDeals,
            variables: {
              request: JSON.stringify({
                categoryID: categoryId,
                stageID: dealStage.id,
                assigneeID:
                  dealFilter.dealOwner === 'all' ? '' : dealFilter.dealOwner,
                searchString,
                limit: 25,
                displayFields,
              }),
            },
          });
          // @ts-ignore
          const parsedData = JSON.parse(res.data.listAllDeals);
          deals = parsedData.deals || [];
          canLoadMore = parsedData.canLoadMore;
        } catch (err) {
          console.error(err);

          GamifyToast.error(
            `There was an error loading more deals for the ${
              normalizedDealStages[dealStage.id].name
            } stage.`
          );

          // Stop the loading animation, set deals to empty array
          const dealsByDealStageStopLoadingPayload = {
            stageId: dealStage.id,
            updates: {
              deals: [],
              currentOffset: 0,
              canLoadMore: false,
              isLoading: false,
            },
          };

          dispatch({
            type: UPDATE_DEAL_STAGE,
            payload: dealsByDealStageStopLoadingPayload,
          });
        }

        const dealsArr: Deal[] = [];

        for (const deal of deals) {
          // Need to get setter/closer profile pictures from imageName (if no imageName, will be an empty string)
          const setterProfilePicture = await getUserAvatar(
            deal.setter.imageName,
            '',
            true
          );
          // @ts-ignore
          deal.setter.profilePicture = setterProfilePicture;

          let closerProfilePicture;

          if (deal.closer) {
            closerProfilePicture = await getUserAvatar(
              deal.closer.imageName,
              '',
              true
            );
            // @ts-ignore
            deal.closer.profilePicture = closerProfilePicture;
          }

          const tempLocalDate = new Date(deal.updatedAt);
          // NOTE: getTimezoneOffset gives the offset from UTC in minutes, converting to ms by multiplying by 60000 is needed
          const timezoneOffset = tempLocalDate.getTimezoneOffset() * 60000;
          // NOTE: timezoneOffset is positive for west of UTC, negative for east of UTC -> subtract this offset to get local time
          const adjustedDate = new Date(
            tempLocalDate.getTime() - timezoneOffset
          );
          // NOTE: toISOString converts the Date object to UTC, since adjustedDate was offset this conversion will make adjustedUpdatedAt the user's local date time
          const adjustedUpdatedAt = adjustedDate.toISOString();

          const unformattedUpdatedAt = adjustedUpdatedAt.split('T');
          const dealYear = unformattedUpdatedAt[0].slice(0, 4);
          const dealMonth = unformattedUpdatedAt[0].slice(5, 7);
          const dealDay = unformattedUpdatedAt[0].slice(8, 10);
          const dealHour = unformattedUpdatedAt[1].slice(0, 2);
          const dealMinute = unformattedUpdatedAt[1].slice(3, 5);
          const updatedAt = `${dealMonth}/${dealDay}/${dealYear} ${dealHour}:${dealMinute}`;

          deal.updatedAt = updatedAt;

          dealsArr.push(deal);
        }

        const dealsByDealStageUpdatePayload = {
          stageId: dealStage.value,
          updates: {
            deals: dealsArr,
            currentOffset: dealsArr.length,
            canLoadMore,
            isLoading: false,
          },
        };

        dispatch({
          type: UPDATE_DEAL_STAGE,
          payload: dealsByDealStageUpdatePayload,
        });
      }
    }, 1000);

    // Only setUpDeals once dealStages and dealTypes are loaded
    if (dealStages.length > 0 && dealTypes.length > 0) {
      return () => clearTimeout(setUpSearchedDeals);
    }
  }, [searchString]);

  if (!areDealStagesLoaded) {
    return <DealsKanbanSkeleton></DealsKanbanSkeleton>;
  }

  return (
    <>
      {selectedDeal && (
        <OwnerDealStageModal
          deal={selectedDeal}
          dealStages={dealStages}
          dealPeople={dealPeople}
          showOwnerDealStageModal={showOwnerDealStageModal}
          setShowOwnerDealStageModal={setShowOwnerDealStageModal}
          triggerSelectedDealRefetch={triggerSelectedDealRefetch}
          onTriggerRefetch={() => {
            // Delay refetch so DB has enough time to update
            setTimeout(() => {
              onTriggerDealsRefetch();
            }, 500);
            onTriggerSelectedDealRefetch();
          }}
        ></OwnerDealStageModal>
      )}
      <Box style={{ display: 'flex' }}>
        <Box
          style={{
            height: '100vh',
            width: selectedDealId ? '55%' : '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: '178px',
            // paddingTop: '88px',
            backgroundColor: '#F0F0F3',
          }}
        >
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              position: 'relative',
              width: '100%',
              paddingLeft: 60,
              paddingRight: 60,
            }}
          >
            <Box style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography
                style={{
                  paddingTop: '44px',
                  fontSize: '36px',
                  fontWeight: 700,
                }}
              >
                Pipeline
              </Typography>
            </Box>

            <Button
              disableRipple={true}
              onClick={
                (e) =>
                  handleNewDeal(
                    e,
                    dealTypes[0],
                    dealTypesCategoryIdMap,
                    dealTypesConfigMap
                  )
                // TO DO: Put back in once dealTypes implemented
                // dealTypes.length > 1
                //   ? handleAddDealClick()
                //   : handleNewDeal(
                //       e,
                //       dealTypes[0],
                //       dealTypesCategoryIdMap,
                //       dealTypesConfigMap
                //     )
              }
              style={{
                display: 'flex',
                justifyContent: 'space-evenly',
                backgroundColor: '#FF6A00',
                color: 'white',
                fontSize: '18px',
                fontWeight: 700,
                width: '154px',
                height: '48px',
                padding: '12px 16px 12px 16px',
                borderRadius: '8px',
                gap: '10px',
              }}
            >
              <GamifyIcon
                icon={'add'}
                color={'white'}
                backgroundColor={'#FF6A00'}
                width={24}
                height={24}
              />
              New deal
            </Button>
          </Box>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              marginTop: 12,
              overflowX: 'auto',
              overflowY: 'hidden',
              height: 'fit-content',
              minHeight: 'fit-content',
            }}
          >
            <Box
              style={{
                paddingLeft: 60,
                display: 'grid',
                columnGap: '16px',
                minWidth: 350,
                // minWidth: 225,
                marginRight: 16,
              }}
              gridTemplateColumns={
                'repeat(1, minmax(0, 1fr))'
                // dealTypes.length > 1
                //   ? 'repeat(3, minmax(0, 1fr))'
                //   : 'repeat(2, minmax(0, 1fr))'
              }
            >
              {/* TO DO: Put back in once dealTypes is implemented */}
              {/* {dealTypes.length > 1 && (
              <Box className={'deal-list-settings-container'}>
                <Typography className={'deal-settings-title-text'}>
                  Type
                </Typography>
                <StyledSelect
                  // style={{ maxWidth: '157px' }}
                  // placeholder="HELLO!!"
                  onChange={(e) => handleDealFilterChange(e)}
                  value={dealFilter.dealType}
                  name="dealType"
                  MenuProps={{
                    MenuListProps: {
                      sx: {
                        boxShadow: '0px 4px 24px 0px #ababab5e;',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        paddingTop: '0px',
                        marginTop: '8px',
                      },
                    },
                  }}
                >
                  {dealTypes.map((opt, i) => (
                    <StyledMenuItem value={opt} key={i}>
                      {capitalizeFirstLetter(opt)}
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
              </Box>
            )} */}
              <Box className={'deal-list-settings-container'}>
                <Typography className={'deal-settings-title-text'}>
                  Who
                </Typography>
                <Box>
                  <Autocomplete
                    id={'deal-autocomplete'}
                    classes={{
                      inputRoot: 'scorecard-dropdown-box',
                      paper: 'autocomplete-menu',
                    }}
                    sx={{ width: 270 }}
                    onChange={(e, val) => {
                      if (userId !== val.value) {
                        // LOG.debug ("@@stageFilter: ", val.id);
                        // console.log('***setSearchStage: ', val);

                        setDealFilter((p) => ({
                          ...p,
                          dealOwner: val.value,
                        }));
                        setUserId(val.value);
                      }
                    }}
                    options={dealPeople}
                    defaultValue={defaultOwner}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              style={{
                paddingRight: 60,
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              <Box
                style={{
                  width: '100% !important',
                  height: '46px',
                  borderRadius: '8px',
                  background: 'white',
                  marginBottom: 2,
                }}
              >
                <SearchInput
                  style={{ minWidth: 502, width: '100%', marginBottom: 0 }}
                  id={'leadSearch'}
                  name={'leadSearch'}
                  placeholder={'Search'}
                  fullWidth
                  onChange={(key) => {
                    console.log(key);
                    // @ts-ignore
                    setSearchString(key?.nativeEvent?.target?.value);
                  }}
                  value={searchString}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position={'start'}
                        sx={{ paddingLeft: '0' }}
                      >
                        <GamifyIcon
                          icon={'search'}
                          color={'#868686'}
                          backgroundColor={'transparent'}
                          height={24}
                          width={24}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: cancelIconAdornment,
                  }}
                  InputLabelProps={{
                    shrink: false,
                    sx: { marginLeft: '24px' },
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box
            style={{
              display: 'flex',
              position: 'relative',
              width: '100%',
              paddingLeft: 60,
              paddingRight: 60,
              paddingTop: 30,
              overflowX: 'auto',
            }}
          >
            {/* NOTE: The Boxes below add the 60px padding on either side of the kanban to make it look better when horizontally scrolling */}
            <Box
              style={{
                position: 'fixed',
                top: 180,
                left: 0,
                width: 60,
                height: '100%',
                backgroundColor: '#F0F0F3',
                pointerEvents: 'none', // Ensure it doesn't interfere with scrolling or interaction
                zIndex: 1,
              }}
            />

            <Box
              style={{
                position: 'fixed',
                top: 180,
                right: selectedDealId ? '45%' : 0,
                width: 60,
                height: '100%',
                backgroundColor: '#F0F0F3',
                pointerEvents: 'none', // Ensure it doesn't interfere with scrolling or interaction
                zIndex: 1,
              }}
            />

            {dealStages.map((dealStage, i) => (
              <Box
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 450,
                  width: 450,
                  marginRight: 16,
                  //   boxSizing: 'content-box',
                  //   paddingLeft: 8,
                  //   paddingRight: 8,
                  //   borderLeft: i === 0 ? '1px solid #D0D2D8' : 'none',
                  //   borderRight: '1px solid #D0D2D8',
                }}
              >
                <Box style={{ display: 'flex' }}>
                  <Typography
                    style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}
                  >
                    {dealStage.label}
                  </Typography>
                </Box>

                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    overflowY: 'auto',
                  }}
                >
                  {!dealsByDealStage[dealStage.id]?.isLoading &&
                    dealsByDealStage[dealStage.id]?.deals?.length === 0 && (
                      <Box
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '30vh',
                        }}
                      >
                        <Typography>
                          There are no deals for this stage.
                        </Typography>
                      </Box>
                    )}
                  {dealsByDealStage[dealStage.id]?.isLoading &&
                  dealsByDealStage[dealStage.id]?.deals?.length === 0 ? (
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '30vh',
                      }}
                    >
                      <CircularProgress
                        sx={{ color: 'black' }}
                      ></CircularProgress>
                    </Box>
                  ) : (
                    // @ts-ignore
                    dealsByDealStage[dealStage.value].deals.map((deal, i) => (
                      <DealKanbanCard
                        key={i}
                        deal={deal}
                        selectedDealId={selectedDealId}
                        handleDealSelection={handleDealSelection}
                        setAddDealOpen={setAddDealOpen}
                        handleOpenOwnerDealStageModal={
                          handleOpenOwnerDealStageModal
                        }
                        onTriggerDealsRefetch={onTriggerDealsRefetch}
                        userId={userId}
                        searchStage={searchStage}
                        dealType={dealTypes[0]} // TO DO: Replace dealTypes[0] with dealType filter selection
                        dealTypesCategoryIdMap={dealTypesCategoryIdMap}
                        dealTypesConfigMap={dealTypesConfigMap}
                        dealFilter={dealFilter}
                        categoryId={categoryId}
                        searchString={searchString}
                        fromDealPage={fromDealPage}
                        setIsLoaded={setIsLoaded}
                      ></DealKanbanCard>
                    ))
                  )}

                  {dealsByDealStage[dealStage.id].canLoadMore && (
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: 12,
                      }}
                    >
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadMore(dealStage.id);
                        }}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-evenly',
                          backgroundColor: '#FF6A00',
                          width: '154px',
                          height: '48px',
                          padding: '12px 16px 12px 16px',
                          borderRadius: '8px',
                        }}
                        sx={{
                          cursor: dealsByDealStage[dealStage.id].isLoading
                            ? 'not-allowed'
                            : 'cursor',
                        }}
                        disableRipple={true}
                        disabled={dealsByDealStage[dealStage.id].isLoading}
                      >
                        {dealsByDealStage[dealStage.id].isLoading ? (
                          <CircularProgress
                            style={{ height: 24, width: 24 }}
                            sx={{ color: 'white' }}
                          ></CircularProgress>
                        ) : (
                          <Typography
                            style={{
                              color: 'white',
                              fontSize: '16px',
                              fontWeight: 500,
                            }}
                          >
                            Load More
                          </Typography>
                        )}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        {selectedDealId && (
          <Box
            style={{
              height: '100vh',
              width: '45%',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              paddingTop: '178px',
              // paddingTop: '88px',
              backgroundColor: 'white',
            }}
          >
            <Box className={'deal-selected-card'}>
              <Box style={{ position: 'absolute', top: 188, right: 24 }}>
                <Button
                  className={'ellipsis-button'}
                  disableRipple={true}
                  onClick={() => {
                    setSelectedDealId(null);
                    setSelectedDeal(null);
                    navigate('/pipeline/kanban');
                  }}
                >
                  <GamifyIcon
                    icon={'x'}
                    color={'white'}
                    backgroundColor={''}
                    height={12}
                    width={12}
                  />
                </Button>
              </Box>
              <DealItem
                // className={'deal-selected-card-item'}
                props={{
                  dealId: selectedDealId,
                  searchStage,
                  searchUserId: userId,
                }}
                stateProps={{
                  setAddDealOpen,
                  setSortOpen,
                  // selectedDealOptionsOpen,
                  // setSelectedDealOptionsOpen,
                  selectedDealMetaDataOpen,
                  setSelectedDealMetaDataOpen,
                  // setShowEditDealFormModal,
                  handleOpenOwnerDealStageModal,
                  getData,
                  onTriggerDealsRefetch,
                  triggerSelectedDealRefetch,
                  onTriggerSelectedDealRefetch,
                  userId,
                  searchStage,
                  dealType: dealTypes[0], // TO DO: Replace dealTypes[0] with dealType filter selection
                  dealTypesCategoryIdMap,
                  dealTypesConfigMap,
                  dealFilter,
                  categoryId,
                  searchString,
                  fromDealPage,
                  setIsLoaded,
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}
